/**
 * Severidade, em um lugar só.
 *
 * O mapa de rótulo e o de cor estavam copiados em DEZ arquivos — cada tela
 * com a sua versão, algumas em inglês (`critical`), outras em português, com
 * tons diferentes para a mesma coisa. Enquanto os níveis não mudavam,
 * ninguém notava; acrescentar `disaster` (v36) tornaria a divergência
 * imediata e silenciosa em qualquer tela que ficasse para trás.
 *
 * É a mesma lição de `ruleKinds.ts`, que já tinha custado seis dos doze kinds
 * sumindo de uma tela: `Record<Severity, …>` faz o TypeScript recusar o build
 * se um nível ficar sem nome ou sem cor. A garantia só vale se existir um
 * mapa, não dez.
 */

import type { Severity } from "@/types";

/**
 * Rótulos em inglês, de propósito.
 *
 * Info / Warning / Critical / Disaster é o vocabulário que Zabbix, Nagios,
 * PagerDuty e a maior parte do mercado de monitoramento usa. Quem opera
 * infraestrutura já lê essas quatro palavras sem pensar, e traduzir deixaria
 * o EnvPulse com um vocabulário próprio para um conceito que já tem um
 * padrão — o custo de aprender recai sobre o cliente.
 *
 * É a exceção consciente ao resto da interface, que é em português.
 */
export const SEVERIDADE_LABEL: Record<Severity, string> = {
  info: "Info",
  warning: "Warning",
  critical: "Critical",
  disaster: "Disaster",
};

/** Tom do Badge. `disaster` tem cor própria: se dividisse o vermelho com
 *  `critical`, o nível novo não teria razão de existir na tela — num painel
 *  cheio, a diferença precisa ser visível antes de alguém ler o texto. */
export const SEVERIDADE_TOM: Record<Severity, "neutral" | "warning" | "danger" | "emergency"> = {
  info: "neutral",
  warning: "warning",
  critical: "danger",
  disaster: "emergency",
};

/** Ordem, para ordenar lista e escolher a pior de um conjunto. */
export const SEVERIDADE_RANK: Record<Severity, number> = {
  info: 1,
  warning: 2,
  critical: 3,
  disaster: 4,
};

export const SEVERIDADE_OPCOES: { value: Severity; label: string }[] =
  (Object.keys(SEVERIDADE_LABEL) as Severity[]).map((s) => ({
    value: s,
    label: SEVERIDADE_LABEL[s],
  }));


/**
 * Opções de "severidade mínima" — piso, não igualdade.
 *
 * Telas de inscrição e de preferência de push escolhem A PARTIR DE qual
 * nível avisar, e o rótulo precisa dizer isso: "Warning" sozinho sugeriria
 * que só warning passa. Estavam escritas à mão em três lugares, cada uma
 * com uma redação, e a lista parava em `critical`.
 */
export const SEVERIDADE_MINIMA_OPCOES: { value: Severity; label: string }[] = [
  { value: "info",     label: "Info (tudo)" },
  { value: "warning",  label: "Warning para cima" },
  { value: "critical", label: "Critical para cima" },
  { value: "disaster", label: "Só Disaster" },
];
